import React, { useState } from 'react';
import { Text, Spinner, makeStyles, tokens, Badge } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchExportKpis, fetchExportShipments, type ShipmentExportStatus } from '../api/export.api';
import { fetchAgingReport, fetchCustomers, AGING_BUCKETS } from '../api/sales.api';
import { KpiCard } from '../components/shared/KpiCard';
import { ShipmentTemperatureChart } from '../components/logistics/ShipmentTemperatureChart';
import { ShipmentRouteMap } from '../components/logistics/ShipmentRouteMap';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { useRanchoStore } from '../store/rancho.store';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * F7 — "Logística y Cobros" del portal inversionista.
 * Reusa ExportModule (mismos endpoints que ExportPage) para tracking de
 * embarques, temperatura, estados de aduana/FDA, y F3 (aging.ts) para la
 * cartera vencida — ambos ya existen en el backend.
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
  td: { padding: '8px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: tokens.fontSizeBase200 },
  pending: {
    padding: '14px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  bucketRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
});

const STATUS_COLOR: Record<ShipmentExportStatus, 'success' | 'warning' | 'danger' | 'subtle'> = {
  DRAFT:              'subtle',
  PACKING:            'subtle',
  SEALED:             'warning',
  DEPARTED:           'warning',
  CUSTOMS_SUBMITTED:  'warning',
  CUSTOMS_CLEARED:    'success',
  CUSTOMS_HOLD:       'danger',
  FDA_SUBMITTED:      'warning',
  FDA_CLEARED:        'success',
  FDA_HOLD:           'danger',
  ARRIVED:            'success',
  DELIVERED:          'success',
  CANCELLED:          'subtle',
};

export function InvestorLogisticsPage() {
  const s = useStyles();
  const t = useT();
  const { fmtNum } = useIntlFormat();
  const { formatCurrency } = useLocaleSettings();

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);
  const { data: kpis, isLoading: kpisLoading } = useQuery({
    queryKey: ['investor-logistics-kpis', activeRanchoId],
    queryFn: () => fetchExportKpis(activeRanchoId),
  });
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['investor-logistics-shipments', activeRanchoId],
    queryFn: () => fetchExportShipments(undefined, 10, activeRanchoId),
  });
  const { data: aging, isLoading: agingLoading } = useQuery({
    queryKey: ['investor-logistics-aging', activeRanchoId],
    queryFn: () => fetchAgingReport(undefined, activeRanchoId),
  });

  const { data: customers } = useQuery({ queryKey: ['investor-logistics-customers'], queryFn: fetchCustomers });
  const customerName = (id: string) => customers?.find(c => c.customerId === id)?.name ?? id;

  const shipments = listData?.shipments ?? [];
  // Cada moneda de la cartera se muestra en su propia moneda, no en la base del tenant.
  const fmt = (n?: number, code?: string) => formatCurrency(n ?? 0, code);

  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const selected = shipments.find(sh => sh.shipmentId === selectedShipmentId);

  function statusLabel(st: ShipmentExportStatus): string {
    const key = `status${st}` as keyof typeof t.export;
    return String(t.export[key] ?? st);
  }

  return (
    <div className={s.root}>
      <PageHeader title={t.investorLogistics.title} subtitle={t.investorLogistics.subtitle} />

      {kpisLoading && <Spinner size="small" label={t.export.loading} />}
      {kpis && (
        <div className={s.kpiRow}>
          <KpiCard label={t.export.kpiTotal} value={String(kpis.totalShipments)} />
          <KpiCard label={t.export.kpiInTransit} value={String(kpis.activeShipments)} />
          <KpiCard label={t.export.kpiHeld} value={String(kpis.onHoldShipments)} accent={kpis.onHoldShipments > 0} />
          <KpiCard label={t.export.kpiDelivered} value={String(kpis.deliveredShipments)} />
          <KpiCard label={t.export.kpiKg} value={fmtNum(kpis.exportKgTotal)} />
          {kpis.avgTransitDays != null && (
            <KpiCard label={t.export.kpiTransitDays} value={`${kpis.avgTransitDays.toFixed(1)} ${t.export.daysUnit}`} />
          )}
          {kpis.tempAnomalies > 0 && (
            <KpiCard label={t.investorLogistics.kpiTempAnomalies} value={kpis.tempAnomalies} accent />
          )}
          {kpis.delayCount > 0 && (
            <KpiCard label={t.investorLogistics.kpiDelays} value={kpis.delayCount} accent />
          )}
        </div>
      )}

      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorLogistics.shipmentsTitle}</span>
        {listLoading && <Spinner size="small" label={t.export.loading} />}
        {!listLoading && shipments.length === 0 && <Text size={200}>{t.export.noData}</Text>}
        {shipments.length > 0 && (
          <table className={s.table}>
            <thead>
              <tr>
                <th className={s.th}>{t.export.tableContainer}</th>
                <th className={s.th}>{t.export.tableProduct}</th>
                <th className={s.th}>{t.export.tableDestination}</th>
                <th className={s.th} style={{ textAlign: 'right' }}>{t.export.tableKg}</th>
                <th className={s.th}>{t.export.tableStatus}</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map(sh => (
                <tr
                  key={sh.shipmentId}
                  onClick={() => setSelectedShipmentId(sh.shipmentId)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className={s.td}>{sh.containerId ?? '—'}</td>
                  <td className={s.td}>{sh.product ?? '—'}</td>
                  <td className={s.td}>{sh.destinationCountry ?? '—'}</td>
                  <td className={s.td} style={{ textAlign: 'right' }}>{fmtNum((sh.actualKg ?? sh.estimatedKg ?? 0))}</td>
                  <td className={s.td}>
                    <Badge appearance="tint" color={STATUS_COLOR[sh.status]}>{statusLabel(sh.status)}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={s.section}>
        <Text className={s.sectionTitle}>{t.investorLogistics.telemetryTitle}</Text>
        {!selected ? (
          <div className={s.pending}>{t.investorLogistics.selectShipment}</div>
        ) : (
          <>
            <ShipmentRouteMap shipmentId={selected.shipmentId} />
            <ShipmentTemperatureChart
              shipmentId={selected.shipmentId}
              tempMinC={selected.tempMinC}
              tempMaxC={selected.tempMaxC}
            />
          </>
        )}
      </div>

      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorLogistics.collectionsTitle}</span>
        {agingLoading && <Spinner size="small" label={t.export.loading} />}
        {aging && aging.currencies.length === 0 && <Text size={200}>{t.export.noData}</Text>}
        {aging && aging.currencies.map(cur => {
          const g = aging.byCurrency[cur];
          return (
            <React.Fragment key={cur}>
              <Text size={200} weight="semibold">{cur}</Text>
              <div className={s.kpiRow}>
                <KpiCard label={t.investorLogistics.agingCurrent} value={fmt(g.current, cur)} />
                {AGING_BUCKETS.map(b => (
                  <KpiCard
                    key={b}
                    label={`${t.investorLogistics.agingBucketPrefix} ${b}`}
                    value={fmt(g.buckets[b], cur)}
                    accent={b === '90+' && g.buckets[b] > 0}
                  />
                ))}
                <KpiCard label={t.investorLogistics.agingTotal} value={fmt(g.totalOutstanding, cur)} />
              </div>
              {g.byCustomer.length > 0 && (
                <table className={s.table}>
                  <thead>
                    <tr>
                      <th className={s.th}>{t.investorLogistics.tableCustomer}</th>
                      <th className={s.th} style={{ textAlign: 'right' }}>{t.investorLogistics.agingCurrent}</th>
                      {AGING_BUCKETS.map(b => (
                        <th key={b} className={s.th} style={{ textAlign: 'right' }}>{b}</th>
                      ))}
                      <th className={s.th} style={{ textAlign: 'right' }}>{t.investorLogistics.agingTotal}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.byCustomer.map(c => (
                      <tr key={c.customerId}>
                        <td className={s.td}>{customerName(c.customerId)}</td>
                        <td className={s.td} style={{ textAlign: 'right' }}>{fmt(c.current, cur)}</td>
                        {AGING_BUCKETS.map(b => (
                          <td key={b} className={s.td} style={{ textAlign: 'right' }}>{fmt(c.buckets[b], cur)}</td>
                        ))}
                        <td className={s.td} style={{ textAlign: 'right' }}>{fmt(c.totalOutstanding, cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
